<template>
  <div class="app-subscription-user-plan" v-loading="dataLoading">

    <!-- 工具栏 -->
    <div class="toolbar">
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openDialog">New</el-button>
      <div class="filter-right">
        <el-input
          size="small"
          v-model="listQuery.q"
          placeholder="Nickname / Phone No"
          style="width: 240px; margin-right: 10px;"
          @keyup.enter.native="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
        </el-input>
      </div>
    </div>

    <!-- 历史记录表格 -->
    <el-table :data="tableData" row-key="id">
      <el-table-column prop="id" label="ID" width="65" />
      <el-table-column label="Nickname" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.nickname || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="phoneNo" label="Phone No" min-width="130" />
      <el-table-column prop="planName" label="Plan Name" min-width="120" />
      <el-table-column label="Price Type" width="110" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.priceType === 'fixed' ? 'primary' : 'success'">
            {{ scope.row.priceType }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Discount (%)" width="110" align="right">
        <template slot-scope="scope">
          {{ (scope.row.priceType === 'discount' && (scope.row.discount !== null && scope.row.discount !== undefined)) ? scope.row.discount : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="Price (¥)" width="100" align="right">
        <template slot-scope="scope">
          {{ (scope.row.priceType === 'fixed' && (scope.row.priceCents !== null && scope.row.priceCents !== undefined)) ? (scope.row.priceCents / 100).toFixed(2) : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="Created At" min-width="175">
        <template slot-scope="scope">
          <i class="el-icon-time" />
          <span style="margin-left: 8px">{{ scope.row.createdAt | displayTime() }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="80" align="center">
        <template slot-scope="scope">
          <el-button type="danger" size="mini" icon="el-icon-delete" circle @click="handleDelete(scope.row)" />
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-show="total > 0"
      :total="total"
      :offset.sync="listQuery.offset"
      :limit.sync="listQuery.limit"
      layout="sizes, prev, pager, next, total"
      @pagination="fetchData"
    />

    <!-- User Specific Pricing 对话框 -->
    <el-dialog title="User Specific Pricing" :visible.sync="dialogVisible" width="650px" @close="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-width="130px" size="small">

        <el-form-item label="Plan" prop="productId">
          <el-select v-model="form.productId" placeholder="Select a plan" style="width: 100%">
            <el-option
              v-for="plan in planOptions"
              :key="plan.productId"
              :label="plan.name + ' (' + plan.productId + ')'"
              :value="plan.productId"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Price Type" prop="priceType">
          <el-radio-group v-model="form.priceType">
            <el-radio label="discount">Discount</el-radio>
            <el-radio label="fixed">Fixed Price</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.priceType === 'discount'" label="Discount (%)" prop="discount">
          <el-input-number v-model="form.discount" :min="0" :max="100" :step="5" style="width: 200px" />
          <div class="field-hint">e.g. 80 means 20% off</div>
        </el-form-item>

        <el-form-item v-if="form.priceType === 'fixed'" label="Price (¥)" prop="priceYuan">
          <el-input-number v-model="form.priceYuan" :min="0" :precision="2" :step="1" style="width: 200px" />
        </el-form-item>

        <el-form-item label="Select Users" prop="appUserIds">
          <app-user-selection-multi ref="userSelection" :ids="form.appUserIds" :no-pre-login="true" @select="handleUserSelect" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchAppSubscriptionPlans,
  fetchAppSubscriptionsUserPlan,
  upsertAppSubscriptionsUserPlan,
  deleteAppSubscriptionsUserPlan,
} from "@/api/admin";
import AppUserSelectionMulti from "@/components/admin/AppUserSelectionMulti.vue";
import Pagination from "@/components/Pagination";

export default {
  name: "AppTabSubscriptionUserPlan",
  components: { AppUserSelectionMulti, Pagination },
  data() {
    return {
      // 历史记录列表
      tableData: [],
      total: 0,
      dataLoading: false,
      listQuery: {
        q: "",
        offset: 0,
        limit: 10,
      },

      // 套餐选项（用于弹窗下拉）
      planOptions: [],

      // 弹窗
      dialogVisible: false,
      saving: false,
      form: this.defaultForm(),
      rules: {
        productId: [
          { required: true, message: "Please select a plan", trigger: "change" },
        ],
        appUserIds: [
          { type: "array", required: true, message: "Please select at least one user", trigger: "change" },
        ],
      },
    };
  },
  mounted() {
    this.active();
  },
  methods: {
    active() {
      this.fetchData();
      this.fetchPlanOptions();
    },
    fetchData() {
      this.dataLoading = true;
      const params = { ...this.listQuery };
      if (!params.q) delete params.q;
      fetchAppSubscriptionsUserPlan(params)
        .then((response) => {
          if (response.success) {
            this.tableData = response.result.items || [];
            this.total = response.result.totalCount || 0;
          }
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    fetchPlanOptions() {
      fetchAppSubscriptionPlans().then((response) => {
        if (response.success) {
          this.planOptions = response.result.items || [];
        }
      });
    },
    handleSearch() {
      this.listQuery.offset = 0;
      this.fetchData();
    },
    defaultForm() {
      return {
        productId: "",
        priceType: "discount",
        discount: 80,
        priceYuan: 0,
        appUserIds: [],
      };
    },
    openDialog() {
      this.form = this.defaultForm();
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
        this.$refs.userSelection && this.$refs.userSelection.reopen();
      });
    },
    resetForm() {
      this.$refs.form && this.$refs.form.resetFields();
    },
    handleUserSelect(ids) {
      this.form.appUserIds = ids;
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;

        const selectedPlan = this.planOptions.find(p => p.productId === this.form.productId);
        if (selectedPlan && selectedPlan.priceCents > 0) {
          let isTooLow = false;
          if (this.form.priceType === "fixed") {
            const actualPriceCents = Math.round(this.form.priceYuan * 100);
            if (actualPriceCents < selectedPlan.priceCents * 0.4) {
              isTooLow = true;
            }
          } else if (this.form.priceType === "discount") {
            if (this.form.discount < 40) {
              isTooLow = true;
            }
          }

          if (isTooLow) {
            this.$confirm("微信小程序虚拟支付不支持实际价格低于原价40%，确定提交？", "Warning", {
              confirmButtonText: "确定",
              cancelButtonText: "取消",
              type: "warning"
            }).then(() => {
              this.doSubmit();
            }).catch(() => {});
            return;
          }
        }

        this.doSubmit();
      });
    },
    doSubmit() {
      this.saving = true;

      const payload = this.form.appUserIds.map((userId) => {
        const item = {
          appUserId: userId,
          productId: this.form.productId,
          priceType: this.form.priceType,
        };
        if (this.form.priceType === "fixed") {
          item.priceCents = Math.round(this.form.priceYuan * 100);
        } else {
          item.discount = this.form.discount;
        }
        return item;
      });

      upsertAppSubscriptionsUserPlan(payload)
        .then((response) => {
          if (response.success) {
            this.$notify({
              title: "User plan updated",
              type: "success",
            });
            this.dialogVisible = false;
            this.fetchData();
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleDelete(row) {
      this.$confirm("Are you sure you want to delete this user plan?", "Warning", {
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        type: "warning"
      }).then(() => {
        this.dataLoading = true;
        const payload = [{
          appUserId: row.appUserId,
          productId: row.productId
        }];
        deleteAppSubscriptionsUserPlan(payload).then((response) => {
          if (response.success) {
            this.$notify({
              title: "User plan deleted",
              type: "success",
            });
            this.fetchData();
          }
        }).finally(() => {
          this.dataLoading = false;
        });
      }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.app-subscription-user-plan {
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .filter-right {
      display: flex;
      align-items: center;
    }
  }

  .field-hint {
    font-size: 11px;
    color: #909399;
    line-height: 1.2;
    margin-top: 2px;
  }
}
</style>
